import React, {useState, useMemo, useCallback} from 'react';
import _ from 'lodash';
import {map} from 'nanostores';
import {useStore} from '@nanostores/react';
import {Box, Card, Container, LinearProgress, MenuItem, Select, Stack, Tab, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography, useTheme} from '@mui/material';
import withMainContainer from '../components/WithMainContainer.js';
import {useQuery} from '@tanstack/react-query';
import api from '../util/api.js';
import SCTextField from '../components/SCTextField.js';
import SCCheckbox from '../components/SCCheckbox.js';
import {formatTime} from '../util/util.js';
import {DAY, MONTH} from '../util.js';
import SCButton from '../components/SCButton.js';
import {openModal} from '../components/Modals.js';
import SCJsonView from '../components/SCJsonView.js';
import SCDatePicker from '../components/SCDatePicker.js';
import SkeletonWrap from '../components/SkeletonWrap.js';
import Pagination from '../components/Pagination.js';

const $errorsFilter = map({
    search: '',
    allEnvs: false,
    from: new Date(Date.now()-MONTH),
    to: new Date(Date.now()+DAY),
});

const $jobsFilter = map({
    search: '',
    failedOnly: false,
    allEnvs: false,
    type: 'all',
    from: new Date(Date.now()-MONTH),
    to: new Date(Date.now()+DAY),
});

const useJobs = (opt={})=>{
    let {data, isLoading} = useQuery({
        queryKey: ['jobs', opt],
        queryFn: ()=>api.admin.jobs(opt),
    });
    return [data, isLoading];
};

const useJobTypes = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['job_types'],
        queryFn: ()=>api.admin.jobTypes(),
    });
    return [data?.types, isLoading];
};

const useErrors = (opt={})=>{
    let {data, isLoading} = useQuery({
        queryKey: ['errors', opt],
        queryFn: ()=>api.admin.errors(opt),
    });
    return [data, isLoading];
};

const JobTypeDropdown = props=>{
    let {value, onChange: _onChange} = props;
    let [jobTypes, loading] = useJobTypes();
    let onChange = useCallback(ev=>_onChange?.(ev.target.value, ev), [_onChange]);
    return <SkeletonWrap skeleton={loading}>
      <Select value={value} onChange={onChange}>
        <MenuItem value="all">כל הסוגים</MenuItem>
        {jobTypes?.map(type=><MenuItem key={type} value={type}>{type}</MenuItem>)}
      </Select>
    </SkeletonWrap>;
};

const JobsFilterComp = props=>{
    let {store} = props;
    let value = useStore(store);
    return <Stack direction="column" gap={2}>
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <Box maxWidth={400}>
          <SCTextField label="חיפוש" value={value.search}
            onChange={v=>store.setKey('search', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCCheckbox label="רק ג'ובים שכשלו" value={value.failedOnly}
            onChange={v=>store.setKey('failedOnly', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCCheckbox label="כל הסביבות" value={value.allEnvs}
            onChange={v=>store.setKey('allEnvs', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCDatePicker value={value.from} onChange={v=>store.setKey('from', v)}
            label="מתאריך"/>
        </Box>
        <Box maxWidth={400}>
          <SCDatePicker value={value.to} onChange={v=>store.setKey('to', v)}
            label="עד תאריך"/>
        </Box>
        <Stack direction="column" maxWidth={400}>
          <Typography>סוג</Typography>
          <JobTypeDropdown value={value.type} onChange={v=>store.setKey('type', v)}/>
        </Stack>
      </Stack>
    </Stack>;
};

const ErrorsFilterComp = props=>{
    let {store} = props;
    let value = useStore(store);
    return <Stack direction="column" gap={2}>
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
        <Box maxWidth={400}>
          <SCTextField label="חיפוש" value={value.search}
            onChange={v=>store.setKey('search', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCCheckbox label="כל הסביבות" value={value.allEnvs}
            onChange={v=>store.setKey('allEnvs', v)}/>
        </Box>
        <Box maxWidth={400}>
          <SCDatePicker value={value.from} onChange={v=>store.setKey('from', v)}
            label="מתאריך"/>
        </Box>
        <Box maxWidth={400}>
          <SCDatePicker value={value.to} onChange={v=>store.setKey('to', v)}
            label="עד תאריך"/>
        </Box>
      </Stack>
    </Stack>;
};

const TableHeader = props=>{
    let {children} = props;
    return <TableCell align="right">
      <Typography fontWeight="bold" marginTop={1} marginBottom={1}>
        {children}
      </Typography>
    </TableCell>;
};

const JobsTab = props=>{
    let theme = useTheme();
    let filter = useStore($jobsFilter);
    let [page, setPage] = useState(0);
    let [pageSize, setPageSize] = useState(10);
    let opt = useMemo(()=>({...filter, page, pageSize}),
        [filter, page, pageSize]);
    let [jobs, jobsLoading] = useJobs(opt);
    return <Stack direction="column" gap={2}>
      <JobsFilterComp store={$jobsFilter}/>
      {jobsLoading&&<LinearProgress/>}
      <TableContainer component={Card}>
        <Table sx={{minWidth: 650}}>
          <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
            <TableRow>
              <TableHeader>ID</TableHeader>
              <TableHeader>סוג</TableHeader>
              <TableHeader>נוצר ב</TableHeader>
              <TableHeader>ריצה אחרונה</TableHeader>
              <TableHeader>ריצה הבאה</TableHeader>
              <TableHeader>כשלונות</TableHeader>
              <TableHeader>תוכן</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs?.data?.map(j=><TableRow key={j.data.id}>
              <TableCell align="right">
                <Typography>
                  {j.data.id}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {j.name}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {!!j.data.created&&formatTime(j.data.created)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {!!j.lastRunAt&&formatTime(j.lastRunAt)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {!!j.nextRunAt&&formatTime(j.nextRunAt)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title={j.failReason}>
                  <Typography color={j.failCount ? theme.palette.error.main : theme.palette.success.main}>
                    {j.failCount||0}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <SCButton size="small" text="צפייה בתוכן" variant="outlined" onClick={()=>openModal({
                    header: 'תוכן',
                    size: 'lg',
                    content: <SCJsonView value={j.data}/>,
                })}/>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination total={jobs?.total||0} page={page} pageSize={pageSize}
        setPage={setPage} setPageSize={setPageSize}/>
    </Stack>;
};

const ErrorsTab = props=>{
    let filter = useStore($errorsFilter);
    let [page, setPage] = useState(0);
    let [pageSize, setPageSize] = useState(10);
    let opt = useMemo(()=>({...filter, page, pageSize}),
        [filter, page, pageSize]);
    let [errors, errorloading] = useErrors(opt);
    return <Stack direction="column" gap={2}>
      <ErrorsFilterComp store={$errorsFilter}/>
      {errorloading&&<LinearProgress/>}
      <TableContainer component={Card}>
        <Table sx={{minWidth: 650}}>
          <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
            <TableRow>
              <TableHeader>הודעה</TableHeader>
              <TableHeader>נוצר ב</TableHeader>
              <TableHeader>סביבה</TableHeader>
              <TableHeader>תוכן</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {errors?.data?.map(j=><TableRow key={j.id}>
              <TableCell align="right">
                <Typography>
                  {j.message}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {!!j.ts&&formatTime(j.ts)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {j.env}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <SCButton size="small" text="צפייה בתוכן" variant="outlined" onClick={()=>openModal({
                    header: 'תוכן',
                    size: 'lg',
                    content: <SCJsonView value={j.payload}/>,
                })}/>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination total={errors?.total||0} page={page} pageSize={pageSize}
        setPage={setPage} setPageSize={setPageSize}/>
    </Stack>;
};

const TABS = {
    JOBS: {
        label: `ג'ובים`,
        Comp: JobsTab,
    },
    ERRORS: {
        label: 'שגיאות',
        Comp: ErrorsTab,
    },
};

const TabsHeader = props=>{
    let {value, onChange} = props;
    let items = useMemo(()=>_.entries(TABS).map(([k, v])=>({value: k, label: v.label})), []);
    return <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" flexWrap="wrap-reverse">
      <Box flexGrow={1} sx={{borderBottom: 1, borderColor: 'divider'}}>
        <Tabs value={value} onChange={(__, v)=>onChange(v)}>
          {items.map(item=><Tab key={item.value} {...item}/>)}]
        </Tabs>
      </Box>
    </Stack>;
};

const AdminMetrics = ()=>{
    let [tab, setTab] = useState(_.keys(TABS)[0]);
    let Comp = TABS[tab].Comp;
    return <Container maxWidth="lg" sx={{marginTop: 4, marginBottom: 12}}>
      <Stack direction="column" gap={2}>
        <Typography textAlign="center" variant="h2">מטריקות</Typography>
        <TabsHeader value={tab} onChange={setTab}/>
        <Comp/>
      </Stack>
    </Container>;
};

export default withMainContainer(AdminMetrics);
